import React, { useRef, forwardRef, useImperativeHandle, useState } from 'react'
import { getEnv } from '../../utils/env'
import ReCAPTCHA from 'react-google-recaptcha'
import HCaptcha from '@hcaptcha/react-hcaptcha'
import Turnstile from 'react-turnstile'

const ReCaptchaTwo = forwardRef((props, ref) => {
  const { onVerify, forceCaptchaType, invisible = false, onReady } = props
  const recaptchTwoRef = useRef()
  const captchaType = forceCaptchaType || getEnv('REACT_APP_CAPTCHA_TYPE') || 'recaptcha'
  const [captchaToken, setCaptchaToken] = useState(null)

  useImperativeHandle(ref, () => ({
    generateToken: async () => {
      if (captchaType === 'hcaptcha') {
        if (invisible) {
          // For invisible hCaptcha, execute programmatically
          try {
            console.log('Executing invisible hCaptcha...')
            if (!recaptchTwoRef.current) {
              console.error('hCaptcha ref is not available')
              return null
            }
            const result = await recaptchTwoRef.current.execute({ async: true })
            console.log('hCaptcha execute result:', result)
            const token = result?.response || result
            console.log('hCaptcha token:', token)
            return token
          } catch (error) {
            console.error('hCaptcha execute error:', error)
            return null
          }
        } else {
          // For visible captcha, return the token that was set by onVerify callback
          if (captchaToken) {
            return captchaToken
          }
          // If no token yet, wait for user to complete the challenge
          return new Promise((resolve) => {
            const checkToken = setInterval(() => {
              if (captchaToken) {
                clearInterval(checkToken)
                resolve(captchaToken)
              }
            }, 100)
          })
        }
      } else if (captchaType === 'turnstile') {
        if (invisible) {
          // For invisible Turnstile, wait for auto-execution or return existing token
          if (captchaToken) {
            return captchaToken
          }
          // Wait for the onVerify callback to be triggered
          return new Promise((resolve) => {
            const checkToken = setInterval(() => {
              if (captchaToken) {
                clearInterval(checkToken)
                resolve(captchaToken)
              }
            }, 100)
            // Set a timeout to prevent infinite waiting
            setTimeout(() => {
              clearInterval(checkToken)
              resolve(null)
            }, 10000) // 10 second timeout
          })
        } else {
          // For visible captcha, return the token that was set by onVerify callback
          if (captchaToken) {
            return captchaToken
          }
          // If no token yet, wait for user to complete the challenge
          return new Promise((resolve) => {
            const checkToken = setInterval(() => {
              if (captchaToken) {
                clearInterval(checkToken)
                resolve(captchaToken)
              }
            }, 100)
          })
        }
      } else {
        // reCAPTCHA
        const token = await recaptchTwoRef.current.executeAsync()
        recaptchTwoRef.current.reset()
        return token
      }
    },
    reset: () => {
      if (captchaType === 'hcaptcha') {
        setCaptchaToken(null)
        recaptchTwoRef.current.resetCaptcha()
      } else if (captchaType === 'turnstile') {
        setCaptchaToken(null)
        recaptchTwoRef.current.reset()
      } else {
        recaptchTwoRef.current.reset()
      }
    },
    isVerified: () => {
      if (captchaType === 'hcaptcha' || captchaType === 'turnstile') {
        return !!captchaToken
      }
      return true // reCAPTCHA is invisible, always return true
    }
  }))

  const handleCaptchaVerify = (token) => {
    setCaptchaToken(token)
    if (onVerify) {
      onVerify(true)
    }
  }

  const handleCaptchaExpire = () => {
    setCaptchaToken(null)
    if (onVerify) {
      onVerify(false)
    }
  }

  const handleCaptchaReady = () => {
    console.log(`${captchaType} is ready`)
    if (onReady) {
      onReady()
    }
  }

  if (captchaType === 'hcaptcha') {
    return (
      <HCaptcha
        sitekey={getEnv('REACT_APP_HCAPTCHA_SITE_KEY')}
        size={invisible ? "invisible" : "normal"}
        ref={recaptchTwoRef}
        onVerify={handleCaptchaVerify}
        onExpire={handleCaptchaExpire}
        onLoad={handleCaptchaReady}
      />
    )
  }

  if (captchaType === 'turnstile') {
    return (
      <Turnstile
        sitekey={getEnv('REACT_APP_TURNSTILE_SITE_KEY')}
        options={invisible ? {
          appearance: 'execute',
          execution: 'execute'
        } : undefined}
        ref={recaptchTwoRef}
        onVerify={handleCaptchaVerify}
        onExpire={handleCaptchaExpire}
        onLoad={handleCaptchaReady}
      />
    )
  }

  return (
    <ReCAPTCHA
      sitekey={getEnv('REACT_APP_RECAPTHA_SITE_KEY')}
      size="invisible"
      ref={recaptchTwoRef}
      asyncScriptOnLoad={handleCaptchaReady}
    />
  )
})

export default ReCaptchaTwo
